import { Card, Icon, Spinner } from '@iwoca/orion';
import styles from './NewLoanEndedView.module.css';

import {
    FormattedDate,
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import messages from './EndedView.messages';
import InternationalisedName from '../dashboardComponents/InternationalisedName';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

import { getLastRepaymentDate } from './NewLoanEndedView.helpers';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';

import { useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
    CreateNewApplicationForm,
    TApplicationStatus
} from '../NewOutstandingView/CreateNewApplicationForm/CreateNewApplicationForm';

type TProps = { accountId: string } & WrappedComponentProps;

const NewLoanEndedView = ({ accountId }: TProps) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading,
        isError
    } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    if (isLoading || isError) return <Spinner />;

    const loanEndedAccount = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const { cashflows } = loanEndedAccount;
    const lastRepaymentDate = getLastRepaymentDate(cashflows!);

    return (
        <>
            <Card className={styles.loanSummaryHeader}>
                <h1
                    className={styles.welcomeTitle}
                    data-testid="new-loan-ended-view"
                >
                    <FormattedMessage
                        {...messages.newWelcomeBack}
                        values={{
                            name: <InternationalisedName />
                        }}
                    />
                </h1>
                <div className={styles.welcomeSubtitle}>
                    <Icon icon="calendarOutline" />
                    <p>
                        <FormattedMessage
                            {...messages.subtitle}
                            values={{
                                date: (
                                    <strong>
                                        {' '}
                                        <FormattedDate
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                            value={new Date(lastRepaymentDate!)}
                                        />
                                    </strong>
                                )
                            }}
                        />
                    </p>
                </div>
            </Card>
            <Card className={styles.loanSummaryBody}>
                <h2 className={styles.loanSummaryTitle}>
                    <FormattedMessage {...messages.loanSummaryActionTitle} />
                </h2>
                <ul className={styles.loanSummaryItems}>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointOne} />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointTwo} />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointThree} />
                    </li>
                </ul>
                <LoanEndedForm accountId={accountId} />
            </Card>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default injectIntl(NewLoanEndedView);

const LoanEndedForm = ({ accountId }: { accountId: string }) => {
    const [applicationStatus, setApplicationStatus] =
        useState<TApplicationStatus>(null);

    switch (applicationStatus) {
        case 'requires_documents':
            return <Redirect to="/apply-flexi-loan/" />;

        case 'auto_approved':
        case 'auto_declined':
        case 'docs_complete':
            return <Redirect to="/my-offers/" />;

        default:
            return (
                <CreateNewApplicationForm
                    accountId={accountId}
                    setApplicationStatus={setApplicationStatus}
                    amountFieldTitle={
                        <FormattedMessage {...messages.amountInputTitle} />
                    }
                    turnoverFieldTitle={
                        <FormattedMessage {...messages.turnoverInputTitle} />
                    }
                />
            );
    }
};
